import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Card,
    CardContent,
    Box,
    Link,
    IconButton,
    Breadcrumbs,
    Button,
} from '@mui/material'
import useBoolean from 'utils/customHook/useBoolean'
import SimpleInput from 'components/forms/SimpleInput'
import Input from 'components/forms/Input'
import Action from 'components/action/Action'
import { useDispatch, useSelector } from 'react-redux'
import HomeAction from 'home/actions/HomeAction'
import FileAction from './FileAction'
import { normalizePath } from './FtpUtils'
import { map, filter } from 'lodash'
import { Close, CreateNewFolder, UploadFile } from '@mui/icons-material'

const GRID = 'grid'
const LIST = 'list'

const CreateDir = ({ onCreate }) => {
    const { value: isOpen, setFalse: close, setTrue: open } = useBoolean(false)
    const [name, setName] = useState('')

    const handleClose = () => {
        setName('')
        close()
    }

    const handleCreate = () => {
        const clean = name.trim().replaceAll(' ', '_')
        if (!clean) return
        onCreate(clean)
        handleClose()
    }

    return (
        <>
            <Button
                component='label'
                startIcon={<CreateNewFolder sx={{ fontSize: '25px !important' }} />}
                onClick={(e) => {
                    e.stopPropagation()
                    open()
                }}
                sx={{
                    textTransform: 'none',
                    color: 'black',
                    fontWeight: 500,
                    borderRadius: 6,
                    ml: 1
                }}
            >
                {i18n.ftpNewFolder}
            </Button>
            <Dialog
                open={isOpen}
                onClose={handleClose}
                fullWidth
                maxWidth='xs'
            >
                <DialogTitle>{i18n.ftpNewFolder}</DialogTitle>
                <DialogContent sx={{ paddingTop: 1 }}>
                    <Input
                        title={i18n.ftpFolderName}
                        value={name}
                        onChange={setName}
                        onEnterKeyPress={handleCreate}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant='outlined'>
                        {i18n.cancel}
                    </Button>
                    <Button
                        onClick={handleCreate}
                        variant='contained'
                        color='black'
                        disabled={!name}
                    >
                        {i18n.create}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

CreateDir.propTypes = {
    onCreate: PropTypes.func.isRequired,
}

export const FtpBreadcrumbsToolbar = ({
    currentNode,
    crumbs,
    onNavigate,
    viewMode,
    setViewMode,
    onReload,
}) => {
    const dispatch = useDispatch()
    const globalResearch = useSelector(store => store.HomeReducer.globalResearch) || ''

    const handleCreateFolder = useCallback((name) => {
        if (!currentNode) return
        const createPath = normalizePath(currentNode.path, name)
        dispatch(FileAction.createDir(createPath, onReload))
    }, [currentNode, dispatch, onReload])

    const handleFileUpload = useCallback((e) => {
        if (!currentNode) return
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onloadend = (ev) => {
            dispatch(FileAction.uploadFile(currentNode.path, file.name, ev.target.result, onReload))
            e.target.value = ''
        }
        reader.readAsDataURL(file)
    }, [currentNode, dispatch, onReload])

    const isGridView = viewMode === GRID

    const actions = filter([
        currentNode && <CreateDir key='create-dir' onCreate={handleCreateFolder} />,
        currentNode && (
            <Button
                key='upload-file'
                component='label'
                startIcon={<UploadFile sx={{ fontSize: '25px !important' }} />}
                size='small'
                sx={{
                    textTransform: 'none',
                    color: 'Black',
                    fontWeight: 500,
                    borderRadius: 6,
                    ml: 1
                }}
            >
                {i18n.importFile}
                <input
                    type='file'
                    hidden
                    onChange={handleFileUpload}
                />
            </Button>
        ),
        <Action
            key='toggle-view'
            icon={isGridView ? 'view_list' : 'grid_view'}
            tooltip={isGridView ? i18n.ftpSwitchToListView : i18n.ftpSwitchToGridView}
            onClick={() => setViewMode(isGridView ? LIST : GRID)}
            color='Black'
            size='small'
        />
    ], Boolean)

    return (
        <>
            <Card>
                <CardContent>
                    <Stack spacing={0}>
                        <Stack direction='row' alignItems='center' spacing={2} justifyContent='space-between'>
                            <Box sx={{ position: 'relative', width: '33%', minWidth: 250 }}>
                                <SimpleInput
                                    title={i18n.search}
                                    value={globalResearch}
                                    onChange={(val) => dispatch(HomeAction.updateGlobalResearch(val || ''))}
                                    onEnterKeyPress={(val) => dispatch(HomeAction.updateGlobalResearch(val || ''))}
                                />
                                {globalResearch && (
                                    <IconButton
                                        size='small'
                                        onClick={() => dispatch(HomeAction.updateGlobalResearch(''))}
                                        sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', opacity: 0.7 }}
                                    >
                                        <Close fontSize='small' />
                                    </IconButton>
                                )}
                            </Box>
                            <Stack direction='row' spacing={1} alignItems='center'>
                                {actions}
                            </Stack>
                        </Stack>

                    </Stack>
                </CardContent>
            </Card>
            {crumbs && crumbs.length > 0 && (
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                            <Breadcrumbs sx={{ height: '20', background: 'transparent', boxShadow: 'none' }}>
                                {map(crumbs, (crumb, idx) => {
                                    const isLast = idx === crumbs.length - 1
                                    return (
                                        <Link
                                            key={crumb.path}
                                            component='button'
                                            underline='hover'
                                            color={isLast ? 'text.primary' : 'text.secondary'}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                onNavigate(crumb)
                                            }}
                                            sx={{ fontWeight: isLast ? 'bold' : 'normal', fontSize: '0.875rem' }}
                                        >
                                            {(crumb.label || crumb.id)}
                                        </Link>
                                    )
                                })}
                            </Breadcrumbs>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </>
    )
}

FtpBreadcrumbsToolbar.propTypes = {
    currentNode: PropTypes.object,
    viewMode: PropTypes.string.isRequired,
    setViewMode: PropTypes.func.isRequired,
    onReload: PropTypes.func.isRequired,
}